import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'components/FormFields';
import { usersActions } from 'state/modules/users';
import useStyle from './AddPaymentMethodModal.styled';
import Cards from 'react-credit-cards';
import useScript from 'hooks/useScript';
import { integrations } from 'config';

const AddPaymentMethodModal = ({ open, modelId, accountId, handleClick, type }) => {
  useScript(integrations.zuoraResources);
  useScript(integrations.zuoraSecurity);

  const formSchema = Yup.object().shape({
    field_creditCardHolderName: Yup.string().required(i18n.t('common.required_field')),
    cvc: Yup.string().required(i18n.t('common.required_field')),
    expiry: Yup.string()
      .max(7, i18n.t('common.invalid_value'))
      .min(7, i18n.t('common.invalid_value'))
      .required(i18n.t('common.required_field')),
    field_creditCardNumber: Yup.string().required(i18n.t('common.required_field')),
    field_creditCardPostalCode: Yup.string().required(i18n.t('common.required_field')),
    host: Yup.string(),
    method: Yup.string(),
    tenantId: Yup.string(),
    field_creditCardType: Yup.string(),
    token: Yup.string(),
    signature: Yup.string(),
    field_key: Yup.string(),
    encrypted_fields: Yup.string(),
    encrypted_values: Yup.string(),
    field_style: Yup.string(),
    field_type: Yup.string(),
  });

  const { accountBrokers, fetchingAccountBrokers } = useSelector((state) => state.users);

  let currentOrg = window.location.pathname.split('/')[1];

  const classes = useStyle();

  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [focus, setFocus] = useState();
  const [initialValues, setInitialValues] = useState({});
  const [paymentData, setPaymentData] = useState();
  const [disableFields, setDisableFields] = useState(false);
  const formEl = useRef(null);

  useEffect(() => {
    setInitialValues({
      field_creditCardNumber: '',
      field_creditCardHolderName: '',
      field_creditCardType: '',
      cvc: '',
      expiry: '',
      field_creditCardPostalCode: '',
      host: window.location.origin,
      method: 'submitPage',
      id: paymentData?.page_id || '',
      tenantId: paymentData?.tenant_id || '',
      token: paymentData?.token || '',
      signature: paymentData?.signature || '',
      field_key: integrations.zuoraKey,
      encrypted_fields:
        '#field_creditCardNumber#field_cardSecurityCode#field_creditCardExpirationMonth#field_creditCardExpirationYear',
      encrypted_values: '',
      field_style: 'iframe',
      field_passthrough1: type,
      field_passthrough2: modelId,
      field_passthrough3: currentOrg,
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    dispatch(usersActions.fetchAccountBrokers(accountId));
  }, [accountId, dispatch]);

  useEffect(() => {
    if (accountBrokers && accountBrokers?.broker_slug === 'KLK') {
      const data = {};
      accountBrokers.broker_keys.map((val) => {
        return (data[val.key] = val.value);
      });
      setPaymentData(data);
    }
  }, [accountBrokers, dispatch]);

  useEffect(() => {
    if (paymentData) {
      formRef.current.setFieldValue('id', paymentData?.page_id || '');
      formRef.current.setFieldValue('tenantId', paymentData?.tenant_id || '');
      formRef.current.setFieldValue('token', paymentData?.token || '');
      formRef.current.setFieldValue('signature', paymentData?.signature || '');
    }
  }, [paymentData]);

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  const handleInputFocus = (name) => {
    setFocus(name);
  };
  /**
   * encrypt the text using the specified public key.
   * @param text the text to be encrypted.
   * @param key the public key.
   * @returns Base64 encoded encrypted data.
   */
  const encryptText = (text, token) => {
    if (token) {
      try {
        let key = window.pidCryptUtil.decodeBase64(token);
        let rsa = new window.pidCrypt.RSA();
        //ASN1 parsing
        let asn = window.pidCrypt.ASN1.decode(window.pidCryptUtil.toByteArray(key));
        let tree = asn.toHexTree();

        //setting the public key for encryption with retrieved ASN.1 tree
        rsa.setPublicKeyFromASN(tree);

        // Base64 encode and encrypt the string
        let crypted = rsa.encrypt(text);

        return window.pidCryptUtil.encodeBase64(window.pidCryptUtil.convertFromHex(crypted));
      } catch (e) {
        console.info(e);
      }
    }
    // return origin text if unable to encrypt
    return text;
  };

  const buildEncryptedValues = (values) => {
    let creditCardNumber = values.field_creditCardNumber;

    let cardSecurityCode = values.cvc;

    let expiry = values.expiry.split('/');

    let creditCardExpirationMonth = expiry[0];

    let creditCardExpirationYear = expiry[1];

    let unencryptedValues =
      '#' +
      creditCardNumber +
      '#' +
      cardSecurityCode +
      '#' +
      creditCardExpirationMonth +
      '#' +
      creditCardExpirationYear;

    return encryptText(unencryptedValues, integrations.zuoraKey);
  };

  const submit = (values) => {
    let encryptedText = buildEncryptedValues(values);
    formRef.current.setFieldValue('encrypted_values', encryptedText);
    setDisableFields(true);
    formEl.current.submit();
  };

  const handleCallback = (issuer, setFieldValue) => {
    let data;
    switch (issuer) {
      case 'mastercard': {
        data = 'MasterCard';
        break;
      }
      case 'visa': {
        data = 'Visa';
        break;
      }
      case 'amex': {
        data = 'AmericanExpress';
        break;
      }
      case 'discover': {
        data = 'Discover';
        break;
      }
      default:
        data = issuer;
        break;
    }

    setFieldValue('field_creditCardType', data || '');
  };

  const normalizeExpiryField = (e) => {
    const value = e?.target?.value;

    if (!value) {
      return formRef.current.setFieldValue('expiry', '');
    }

    const onlyNums = value.replace(/[^\d]/g, '');

    if (onlyNums.length <= 2) {
      return formRef.current.setFieldValue('expiry', `${onlyNums}`);
    }
    if (onlyNums.length >= 3) {
      return formRef.current.setFieldValue(
        'expiry',
        `${onlyNums.slice(0, 2)}/${onlyNums.slice(2)}`
      );
    }

    return formRef.current.setFieldValue('expiry', onlyNums);
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      loading={fetchingAccountBrokers}
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      enterButtonLabel={i18n.t('payments.add_card')}
      title={i18n.t('payments.add_payment_card')}
      maxWidth={415}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={formSchema}
      >
        {({ handleSubmit, values, setFieldValue }) => (
          <div id="paymentForm">
            <Cards
              cvc={values.cvc || ''}
              expiry={values.expiry || ''}
              focused={focus}
              issuer="visa|mastercard|discovery|amex"
              name={values.field_creditCardHolderName || ''}
              number={values.field_creditCardNumber || ''}
              callback={({ issuer }) => handleCallback(issuer, setFieldValue)}
            />
            <Form
              noValidate
              onSubmit={handleSubmit}
              action={paymentData?.zuora_url}
              method="POST"
              ref={formEl}
              autoComplete="off"
            >
              <Grid
                data-cy="add-payment-method"
                className={classes.paymentMethods}
                container
                spacing={1}
              >
                <Grid item xs={12}>
                  <Field
                    type="text"
                    component={TextField}
                    disabled={disableFields}
                    name="field_creditCardNumber"
                    label={i18n.t('payments.card_number')}
                    onFocus={() => handleInputFocus('number')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    type="text"
                    name="field_creditCardHolderName"
                    component={TextField}
                    label={i18n.t('payments.name')}
                    onFocus={() => handleInputFocus('name')}
                  />
                </Grid>
                <Grid item xs={7}>
                  <Field
                    type="text"
                    name="expiry"
                    attrs={{
                      maxLength: 7,
                    }}
                    onChange={normalizeExpiryField}
                    component={TextField}
                    disabled={disableFields}
                    label={i18n.t('payments.expiry')}
                    onFocus={() => handleInputFocus('expiry')}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Field
                    type="text"
                    name="cvc"
                    component={TextField}
                    disabled={disableFields}
                    label={i18n.t('payments.cvc')}
                    onFocus={() => handleInputFocus('cvc')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    type="text"
                    name="field_creditCardPostalCode"
                    component={TextField}
                    label={i18n.t('payments.zip_code')}
                    onFocus={() => handleInputFocus('field_creditCardPostalCode')}
                  />
                </Grid>
                <Grid item xs={6} style={{ display: 'none' }}>
                  <Field type="text" name="method" component={TextField} />
                  <Field type="text" name="id" component={TextField} />
                  <Field type="text" name="signature" component={TextField} />
                  <Field type="text" name="tenantId" component={TextField} />
                  <Field type="text" name="token" component={TextField} />
                  <Field type="text" name="host" component={TextField} />
                  <Field type="text" name="field_key" component={TextField} />
                  <Field type="text" name="encrypted_fields" component={TextField} />
                  <Field type="text" name="encrypted_values" component={TextField} />
                  <Field type="text" name="field_style" component={TextField} />
                  <Field type="text" name="field_passthrough1" component={TextField} />
                  <Field type="text" name="field_passthrough2" component={TextField} />
                  <Field type="text" name="field_passthrough3" component={TextField} />
                  <Field type="text" name="field_creditCardType" component={TextField} />
                </Grid>
              </Grid>
            </Form>
          </div>
        )}
      </Formik>
    </BaseModal>
  );
};

AddPaymentMethodModal.propTypes = {
  invoices: PropTypes.string,
  open: PropTypes.bool.isRequired,
  id: PropTypes.string,
  accountId: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
};

AddPaymentMethodModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default AddPaymentMethodModal;
